import styled from '@emotion/styled'
import {
  ExtranetContainer,
  MediaQuery,
  formatDateString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacBadge, PharmacH4, PharmacLoadingDots, PharmacText } from '@pharmac/ui'
import { useQuery } from '@tanstack/react-query'
import { Outlet, useParams } from 'react-router-dom'
import { ExtranetParagraph } from '../../components/extranet/ExtranetParagraph'
import { ExtranetViewReturnButton } from '../../components/extranet/ExtranetViewReturnButton'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { api } from '../../services/HttpService'
import { NotFoundPageView } from '../NotFoundPage'

const NewsletterImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 5px;
`

const InfoContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: var(--spacing-small);

  p {
    ${MediaQuery.maxTablet} {
      margin-bottom: var(--spacing-tiny);
    }
  }
`

const Tag = styled(PharmacBadge)`
  ${MediaQuery.maxTablet} {
    margin-bottom: var(--spacing-tiny);
  }
`

export const NewsletterView = () => {
  const { id } = useParams()
  const { i18n, key } = useLanguage(['common'])
  const { minDesktop } = useBreakpoint()

  const { data, isLoading, isRefetching } = useQuery({
    queryKey: ['__getNewsletter', id, i18n.language],
    queryFn: async () =>
      await api.newsletters.getNewsletterById(Number(id as string), { lang: i18n.language }),
    enabled: typeof id === 'string' && !isNaN(+id),
  })

  if (isLoading)
    return (
      <ExtranetWrapper>
        <ExtranetContainer
          marginLargeTop
          flexRow
          justifyCenter
        >
          <PharmacLoadingDots />
        </ExtranetContainer>
      </ExtranetWrapper>
    )

  if (!isLoading && !data && !isRefetching) return <NotFoundPageView />

  if (isLoading || !data) return null

  return (
    <>
      <ExtranetWrapper>
        <ExtranetContainer
          flexColumn
          paddingSmallHorizontal
          paddingLargeBottom={!minDesktop}
          full
        >
          <ExtranetContainer
            full
            flexRow
            paddingMediumVertical
          >
            <ExtranetViewReturnButton />
          </ExtranetContainer>
          <NewsletterImage src={data?.image.desktopLarge} />
          <ExtranetContainer
            full
            paddingMediumVertical
          >
            <InfoContainer>
              <PharmacText paddingSmallRight>{key(data?.category.name)}</PharmacText>
              <PharmacText marginSmallRight>{formatDateString(data?.newsletterDate)}</PharmacText>
              {data?.tags.map((a: any) => (
                <Tag
                  key={a.id}
                  variant="primary"
                  marginSmallRight
                  small
                >
                  {key(a.name)}
                </Tag>
              ))}
            </InfoContainer>
          </ExtranetContainer>

          <ExtranetContainer
            full
            flexColumn
            paddingMediumBottom={!minDesktop}
          >
            <PharmacH4 marginLargeBottom>{key(data?.title)}</PharmacH4>
            <ExtranetParagraph>{key(data?.content)}</ExtranetParagraph>
          </ExtranetContainer>
        </ExtranetContainer>
      </ExtranetWrapper>
      <Outlet />
    </>
  )
}
